import { Archive, Download, Eye, ListChecks, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { StatusChip } from '@/components/ds/StatusChip'
import { Button } from '@/components/ui/Button'
import { DialogClose, DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { IconButton } from '@/components/ui/IconButton'
import { videoDownloadUrl } from '@/api/endpoints/media'
import { useArchiveVideos } from '@/api/endpoints/videos'
import { errorMessage } from '@/lib/errors'
import { formatViewCount } from '@/lib/format'
import { VisibilityDialog } from '@/features/projects/components/VisibilityDialog'
import {
  downloadFilename,
  selectionKey,
  selectionSummary,
  visibilityTargets,
  type SelectedVideo,
} from '@/features/projects/selection'
import styles from './SelectionBar.module.scss'

export type PageSelectionState = 'none' | 'some' | 'all'

export interface SelectionBarProps {
  /** Everything selected, including cards on other pages. */
  selected: readonly SelectedVideo[]
  /** How much of the current page's selectable cards is selected. */
  pageState: PageSelectionState
  /** False when the current page has nothing selectable. */
  canSelectPage: boolean
  onSelectPage: () => void
  onDeselectPage: () => void
  onClear: () => void
  /** Keys the archive removed, so the page drops them from the selection. */
  onArchived: (keys: string[]) => void
  /**
   * O backend sabe trocar a visibilidade no YouTube (a lista traz a chave
   * ``youtube_privacy``). Falso contra o backend antigo: o botão nem aparece.
   */
  visibilitySupported?: boolean
}

function countLabel(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`
}

/**
 * Chrome drops downloads fired in the same tick and asks once per site
 * before allowing several; spacing the clicks keeps every file in the queue.
 */
const DOWNLOAD_SPACING_MS = 400

function startDownloads(files: readonly { url: string; filename: string }[]): void {
  files.forEach((file, i) => {
    window.setTimeout(() => {
      const anchor = document.createElement('a')
      anchor.href = file.url
      // Honoured only same-origin (dev, via the Vite proxy); in production
      // the API's Content-Disposition does the same job.
      anchor.download = file.filename
      anchor.rel = 'noopener'
      anchor.style.display = 'none'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
    }, i * DOWNLOAD_SPACING_MS)
  })
}

/**
 * Floating bar of the grid's multi-select: count, page toggle and the bulk
 * actions. Mounted only while something is selected; Esc clears.
 */
export function SelectionBar({
  selected,
  pageState,
  canSelectPage,
  onSelectPage,
  onDeselectPage,
  onClear,
  onArchived,
  visibilitySupported = false,
}: SelectionBarProps) {
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [visibilityOpen, setVisibilityOpen] = useState(false)
  const downloadable = selected.filter((v) => v.videoPath)
  const summary = selectionSummary(selected)
  const targets = visibilityTargets(selected)

  // Esc clears the selection — unless a dialog is open, which handles its own
  // Esc (closing just the dialog).
  useEffect(() => {
    if (archiveOpen || visibilityOpen) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClear()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [archiveOpen, visibilityOpen, onClear])

  function download() {
    const files = downloadable.flatMap((v) => {
      const filename = downloadFilename(v.title, v.id)
      const url = videoDownloadUrl(v.videoPath, filename)
      return url ? [{ url, filename }] : []
    })
    startDownloads(files)
    const skipped = selected.length - files.length
    toast.success(`Baixando ${countLabel(files.length, 'MP4', 'MP4s')}`, {
      description: [
        files.length > 1 ? 'O navegador pode pedir permissão para baixar vários arquivos.' : null,
        skipped > 0 ? `${countLabel(skipped, 'selecionado não tem', 'selecionados não têm')} MP4.` : null,
      ]
        .filter(Boolean)
        .join(' '),
    })
  }

  return (
    <div className={styles.bar} role="region" aria-label="Seleção de vídeos">
      <span className={styles.count} aria-live="polite">
        {countLabel(selected.length, 'selecionado', 'selecionados')}
      </span>
      <span className={styles.summary} title="Views somadas dos selecionados já publicados, em todas as páginas">
        <span>
          {summary.published} de {selected.length} {summary.published === 1 ? 'publicado' : 'publicados'}
          {summary.scheduled > 0 ? ` · ${countLabel(summary.scheduled, 'agendado', 'agendados')}` : null}
        </span>
        {summary.published > 0 ? <span className={styles.views}>{formatViewCount(summary.views)}</span> : null}
      </span>
      {canSelectPage ? (
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<ListChecks />}
          onClick={pageState === 'all' ? onDeselectPage : onSelectPage}
        >
          {pageState === 'all' ? 'Desmarcar página' : 'Selecionar página'}
        </Button>
      ) : null}
      <div className={styles.spacer} />
      <Button
        variant="secondary"
        size="sm"
        leadingIcon={<Download />}
        disabled={downloadable.length === 0}
        title={downloadable.length === 0 ? 'Nenhum dos selecionados tem MP4' : undefined}
        onClick={download}
      >
        Baixar MP4
      </Button>
      {visibilitySupported ? (
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<Eye />}
          disabled={targets.eligible.length === 0}
          title={targets.eligible.length === 0 ? 'Só vale para vídeos já publicados' : undefined}
          onClick={() => {
            setVisibilityOpen(true)
          }}
        >
          Visibilidade
        </Button>
      ) : null}
      <Button
        variant="secondary"
        size="sm"
        leadingIcon={<Archive />}
        onClick={() => {
          setArchiveOpen(true)
        }}
      >
        Arquivar
      </Button>
      <IconButton aria-label="Limpar seleção" title="Limpar seleção (Esc)" variant="ghost" size="sm" onClick={onClear}>
        <X />
      </IconButton>

      <ArchiveDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        videos={selected}
        onArchived={onArchived}
      />
      {visibilitySupported ? (
        <VisibilityDialog
          open={visibilityOpen}
          onOpenChange={setVisibilityOpen}
          items={targets.eligible.map((v) => ({
            videoId: v.id,
            lang: v.language,
            youtubeId: v.youtubeId,
            title: v.title,
            privacy: v.youtubePrivacy,
          }))}
          scheduled={targets.scheduled}
          unpublished={targets.unpublished}
        />
      ) : null}
    </div>
  )
}

interface ArchiveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  videos: readonly SelectedVideo[]
  onArchived: (keys: string[]) => void
}

function ArchiveDialog({ open, onOpenChange, videos, onArchived }: ArchiveDialogProps) {
  const mutation = useArchiveVideos()
  const onYouTube = videos.filter((v) => v.status === 'publicado' || v.status === 'agendado').length

  function confirm() {
    mutation.mutate(
      videos.map((v) => ({ videoId: v.id, lang: v.language, channel: v.channel })),
      {
        onSuccess: ({ archived, failed }) => {
          onArchived(archived.map((a) => selectionKey({ id: a.videoId, language: a.lang })))
          if (archived.length > 0) {
            toast.success(countLabel(archived.length, 'vídeo arquivado', 'vídeos arquivados'))
          }
          const firstFailure = failed[0]
          if (firstFailure) {
            toast.error(countLabel(failed.length, 'vídeo não foi arquivado', 'vídeos não foram arquivados'), {
              description: `Continuam selecionados. ${errorMessage(firstFailure.error)}`,
            })
          }
          onOpenChange(false)
        },
      },
    )
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!mutation.isPending) onOpenChange(next)
      }}
    >
      <DialogContent
        title={`Arquivar ${countLabel(videos.length, 'vídeo', 'vídeos')}?`}
        description="O MP4, as miniaturas e o project.json de cada um serão removidos do disco. A ação não pode ser desfeita."
        footer={
          <>
            <DialogClose asChild>
              <Button variant="secondary" disabled={mutation.isPending}>
                Cancelar
              </Button>
            </DialogClose>
            <Button onClick={confirm} disabled={mutation.isPending || videos.length === 0}>
              {mutation.isPending ? 'Arquivando...' : 'Arquivar'}
            </Button>
          </>
        }
      >
        {onYouTube > 0 ? (
          <p className={styles.warning}>
            {countLabel(onYouTube, 'já está', 'já estão')} no YouTube — arquivar aqui não tira o vídeo de
            lá.
          </p>
        ) : null}
        <ul className={styles.list}>
          {videos.map((v) => (
            <li key={selectionKey(v)} className={styles.item}>
              <span className={styles.itemTitle}>{v.title}</span>
              <StatusChip status={v.status} privacy={v.youtubePrivacy} />
            </li>
          ))}
        </ul>
        {mutation.isError ? <p className={styles.error}>{errorMessage(mutation.error)}</p> : null}
      </DialogContent>
    </DialogRoot>
  )
}
